import { IAddress } from "common/types";
import { useClinicGroupCountry } from "common/contexts/UIContext";

import { useCurrentDrawer } from "client/common/hooks/stackables";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useClientUserQuery, useCreateAddressMutation } from "client/accounts/hooks";
import { useSubscriptionsQuery } from "client/subscriptions/hooks";
import { useSubscriptionsBulkUpdateAddressDrawer } from "client/subscriptions/stackables";
import { NON_CANCELED_NON_IMPORTED_SUBSCRIPTION_STATUSES } from "client/subscriptions/constants";

import AddressDrawerForm from "../forms/AddressDrawerForm";

const noop = () => undefined;

export type CreateAddressDrawerProps = {
  onSuccess?: (address: IAddress) => void;
  shouldUpdatePrimary?: boolean;
  displaySubscriptionsBulkUpdate?: boolean;
};

const CreateAddressDrawer = ({
  onSuccess = noop,
  shouldUpdatePrimary = false,
  displaySubscriptionsBulkUpdate = false,
}: CreateAddressDrawerProps) => {
  const { close } = useCurrentDrawer();
  const { open: openSubscriptionBulkUpdate } = useSubscriptionsBulkUpdateAddressDrawer();
  const clinicGroupCountry = useClinicGroupCountry();

  const {
    data: clientUser,
    isLoading: isClientUserLoading,
    error: clientUserError,
  } = useClientUserQuery();

  const {
    data: subscriptions = [],
    isLoading: isSubscriptionsLoading,
    error: subscriptionsError,
  } = useSubscriptionsQuery(
    { visible_status: NON_CANCELED_NON_IMPORTED_SUBSCRIPTION_STATUSES },
    { enabled: displaySubscriptionsBulkUpdate },
  );

  const handleCreateSuccess = (newAddress: IAddress) => {
    close();
    onSuccess(newAddress);
    if (displaySubscriptionsBulkUpdate && subscriptions.length >= 1) {
      openSubscriptionBulkUpdate({
        address: newAddress,
        subscriptions,
      });
    }
  };

  const {
    mutate: createAddress,
    isLoading: isCreating,
    error,
  } = useCreateAddressMutation({
    onSuccess: handleCreateSuccess,
  });

  const renderContent = () => {
    if (isClientUserLoading || isSubscriptionsLoading) {
      return <DrawerLayout.Loading />;
    }
    if (!clientUser || clientUserError || subscriptionsError) {
      return (
        <DrawerLayout.FetchError
          entityName="account"
          error={clientUserError || subscriptionsError}
        />
      );
    }
    return (
      <AddressDrawerForm
        initialValues={{
          name: clientUser.full_name ?? "",
          address_one: "",
          address_two: "",
          city: "",
          state: "",
          county: "",
          zipcode: "",
          country: clinicGroupCountry.toString(),
        }}
        onSubmit={(values) => createAddress({ is_primary: shouldUpdatePrimary, ...values })}
        isLoading={isCreating}
        submitError={error}
        submitText="Add address"
      />
    );
  };

  return (
    <DrawerLayout>
      <DrawerLayout.Header title="Add address" onClose={close} />
      {renderContent()}
    </DrawerLayout>
  );
};

export default CreateAddressDrawer;
